import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import viewer_time_zone from '../../../utils/viewer_time_zone';
import TaskCreateModal from './TaskCreateModal';
import CorpusSources from './CorpusSources';

/**
 * Pick a display label for a chat message author. Prefers "First Last", then
 * email, then a truncated user id. Used on SHARED threads where the rep
 * needs to see who said what. Returns "—" if the userId isn't in the authors
 * map (admins / non-CS users who don't have a CsExecutive row).
 */
function format_author(user_id, authors) {
    if (!user_id) return '—';
    const a = authors && authors[user_id];
    if (!a) return user_id.slice(0, 8);
    const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    if (a.email) return a.email;
    return user_id.slice(0, 8);
}

/**
 * AI chat drawer with two modes (Talk/Act) and two scopes (per-client /
 * portfolio):
 *
 *   scope='client'    — pass store_id. Sessions live under
 *                       /api/v1/clients/:storeId/chat/sessions
 *   scope='portfolio' — used on the My Work page. Sessions live under
 *                       /api/v1/cs/me/chat/sessions; the chat sees the
 *                       rep's whole owned book as context.
 *
 * Talk/Act:
 *   TALK (default) — LLM has no act tools, can't propose writes.
 *   ACT — LLM additionally has propose_status_change + propose_refinement.
 *         Proposals never auto-apply; they surface as cards the rep confirms.
 *
 * Apply path writes through the same audit semantics as manual UI
 * (Finding/Actionable PATCH + RepInput + history). Safety boundary is
 * technical: in TALK mode the act tools simply aren't in the LLM's tool list.
 * Ownership is enforced server-side for portfolio scope.
 */
/**
 * `portfolio_scope` (only meaningful when scope='portfolio') is the rep's
 * active MyWork tab ('my' | 'team' | 'all') at the time a message is sent.
 * After the 2026-06-06 schema migration there's one portfolio session per
 * user; each USER message carries scope on ChatMessage.scopeAtSend so the
 * AI can reason per-turn. Frontend renders a small chip next to each USER
 * message reflecting the scope it was sent under.
 */
const ChatDrawer = ({ store_id, scope = 'client', portfolio_scope = 'my', open, on_close }) => {
    const is_portfolio = scope === 'portfolio';
    const sessions_path = is_portfolio
        ? '/api/v1/cs/me/chat/sessions'
        : `/api/v1/clients/${encodeURIComponent(store_id || '')}/chat/sessions`;
    const [session_id, set_session_id] = useState(null);
    const [mode, set_mode] = useState('TALK');
    // Per-org chats can be either SHARED (team working memory) or PRIVATE
    // (rep's scratchpad) — see docs/design-chat-and-access.md §2. Default
    // to SHARED for client scope; portfolio is always PRIVATE per design.
    const [visibility, set_visibility] = useState(is_portfolio ? 'PRIVATE' : 'SHARED');
    const [messages, set_messages] = useState([]);
    const [proposals, set_proposals] = useState([]);
    // AI-proposed task (per-client) → opens TaskCreateModal pre-filled so the
    // rep reviews + creates it LIVE. No staging card (reps never applied those).
    const [task_modal, set_task_modal] = useState({ open: false, prefill: null, storeId: null });
    const [draft, set_draft] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    // authors: { [userId]: { firstName, lastName, email } } — returned by
    // GET /chat/sessions/:id/messages so SHARED threads can label each
    // turn's author without an extra fetch.
    const [authors, set_authors] = useState({});
    const scroll_ref = useRef(null);

    const load_messages = useCallback(async (sid) => {
        try {
            const resp = await cs_analysis_fetch({ path: `/api/v1/chat/sessions/${sid}/messages` });
            set_messages((resp && resp.messages) || []);
            set_authors((resp && resp.authors) || {});
            set_mode((resp && resp.session && resp.session.mode) || 'TALK');
        } catch (e) {
            set_err(e.message || String(e));
        }
    }, []);

    const load_proposals = useCallback(async (sid) => {
        if (!sid) return;
        try {
            const resp = await cs_analysis_fetch({ path: `/api/v1/chat/sessions/${sid}/proposals` });
            set_proposals(Array.isArray(resp) ? resp : []);
        } catch (e) {
            // proposals are non-critical; don't surface errors
        }
    }, []);

    const ensure_session = useCallback(async () => {
        if (!is_portfolio && !store_id) return;
        set_err(null);
        try {
            const sessions = await cs_analysis_fetch({ path: sessions_path });
            // For client scope, the list contains both SHARED and PRIVATE org
            // sessions; pick the one matching the active visibility toggle.
            // Portfolio sessions are inherently per-user so the list is already
            // narrowed server-side.
            const candidate = Array.isArray(sessions)
                ? (is_portfolio
                    ? sessions[0]
                    : sessions.find((s) => s.visibility === visibility))
                : null;
            if (candidate) {
                set_session_id(candidate.id);
                set_mode(candidate.mode || 'TALK');
                await load_messages(candidate.id);
                await load_proposals(candidate.id);
            } else {
                const resp = await cs_analysis_fetch({
                    path: sessions_path,
                    method: 'POST',
                    body: is_portfolio ? {} : { visibility },
                });
                set_session_id(resp.sessionId);
                set_messages([]);
                set_proposals([]);
            }
        } catch (e) {
            set_err(e.message || String(e));
        }
    }, [is_portfolio, store_id, sessions_path, visibility, load_messages, load_proposals]);

    useEffect(() => {
        if (open && !session_id) ensure_session();
    }, [open, session_id, ensure_session]);

    // When the rep flips the Shared/Private toggle, drop the current session
    // id so the next ensure_session call opens the other thread.
    const switch_visibility = useCallback((next) => {
        if (next === visibility) return;
        set_visibility(next);
        set_session_id(null);
        set_messages([]);
        set_proposals([]);
    }, [visibility]);

    useEffect(() => {
        if (scroll_ref.current) {
            scroll_ref.current.scrollTop = scroll_ref.current.scrollHeight;
        }
    }, [messages, busy]);

    const toggle_mode = async () => {
        if (!session_id) return;
        const next = mode === 'ACT' ? 'TALK' : 'ACT';
        // Optimistic update; revert on error
        set_mode(next);
        try {
            await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/mode`,
                method: 'PATCH',
                body: { mode: next },
            });
        } catch (e) {
            set_mode(mode);
            set_err(e.message || String(e));
        }
    };

    const apply_proposal = async (id) => {
        try {
            await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/proposals/apply`,
                method: 'POST',
                body: { ids: [id] },
            });
            set_proposals((curr) => curr.filter((p) => p.id !== id));
        } catch (e) {
            set_err(e.message || String(e));
        }
    };
    const dismiss_proposal = async (id) => {
        try {
            await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/proposals/${id}/dismiss`,
                method: 'POST',
            });
            set_proposals((curr) => curr.filter((p) => p.id !== id));
        } catch (e) {
            set_err(e.message || String(e));
        }
    };
    const apply_all = async () => {
        if (proposals.length === 0) return;
        try {
            await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/proposals/apply`,
                method: 'POST',
                body: { ids: proposals.map((p) => p.id) },
            });
            set_proposals([]);
        } catch (e) {
            set_err(e.message || String(e));
        }
    };

    const send = async () => {
        if (!draft.trim() || busy || !session_id) return;
        const content = draft.trim();
        set_draft('');
        set_busy(true);
        set_err(null);

        const optimistic = {
            id: 'tmp-' + Date.now(),
            role: 'USER',
            content,
            scopeAtSend: is_portfolio ? portfolio_scope : null,
            createdAt: new Date().toISOString(),
        };
        set_messages((prev) => [...prev, optimistic]);
        const viewer_zone = viewer_time_zone();

        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/messages`,
                method: 'POST',
                body: {
                    content,
                    // Portfolio: tag each message with the rep's active tab so
                    // the AI can reason per-turn scope. Ignored server-side for
                    // non-portfolio sessions.
                    ...(is_portfolio ? { portfolioScope: portfolio_scope } : {}),
                    // The viewer's own zone: a chat with no client says
                    // "today" in it (absent = UTC on the server).
                    ...(viewer_zone ? { viewerTimeZone: viewer_zone } : {}),
                },
            });
            // Merge the sender's display info so the just-sent turn resolves to
            // a name on SHARED (client) threads instead of a truncated id.
            if (resp.authors) set_authors((prev) => ({ ...prev, ...resp.authors }));
            set_messages((prev) => {
                const without_tmp = prev.filter((m) => m.id !== optimistic.id);
                return [
                    ...without_tmp,
                    {
                        id: resp.userMessageId,
                        role: 'USER',
                        userId: resp.userMessageUserId,
                        content,
                        scopeAtSend: is_portfolio ? portfolio_scope : null,
                        createdAt: new Date().toISOString(),
                        becameRepInputId: resp.observationPromoted ? 'pending' : null,
                    },
                    {
                        id: resp.assistantMessageId,
                        role: 'ASSISTANT',
                        content: resp.reply,
                        citations: resp.corpusCitations || [],
                        createdAt: new Date().toISOString(),
                    },
                ];
            });
            // If the reply included proposals, refresh
            if (resp.proposalCount && resp.proposalCount > 0) {
                await load_proposals(session_id);
            }
            // If the AI proposed a task (per-client), open the create-task modal
            // pre-filled so the rep reviews + creates it LIVE (no staging card).
            if (Array.isArray(resp.taskDrafts) && resp.taskDrafts.length > 0) {
                const d = resp.taskDrafts[0];
                set_task_modal({ open: true, prefill: d, storeId: d.clientStoreId || store_id });
            }
        } catch (e) {
            set_messages((prev) => prev.filter((m) => m.id !== optimistic.id));
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    if (!open) return null;
    const act = mode === 'ACT';

    return (
        <div
            style={{
                position: 'fixed',
                top: 80,
                right: 16,
                bottom: 16,
                width: 460,
                background: '#ffffff',
                border: '1px solid #dfe4ea',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1050,
            }}
        >
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #ecf0f1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>
                        AI chat — {is_portfolio ? 'your portfolio' : 'this client'}
                    </span>
                    <Badge color={act ? 'warning' : 'success'} style={{ cursor: 'pointer' }} onClick={toggle_mode} title={`Click to switch to ${act ? 'TALK' : 'ACT'} mode`}>
                        {act ? '⚡ Act' : '🗣 Talk'}
                    </Badge>
                    {/* Shared/Private visibility toggle. Hidden for portfolio
                        scope (always private per design doc §2). Flipping the
                        toggle resets the session id so ensure_session opens
                        the other thread. */}
                    {!is_portfolio ? (
                        <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', border: '1px solid #dfe4ea' }}>
                            <button
                                type='button'
                                onClick={() => switch_visibility('SHARED')}
                                title='Team-visible thread — any CS member can read + write'
                                style={{
                                    border: 'none',
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    background: visibility === 'SHARED' ? '#3498db' : '#fff',
                                    color: visibility === 'SHARED' ? '#fff' : '#2c3e50',
                                }}
                            >
                                Shared
                            </button>
                            <button
                                type='button'
                                onClick={() => switch_visibility('PRIVATE')}
                                title='Your personal scratchpad — only you can see this thread'
                                style={{
                                    border: 'none',
                                    borderLeft: '1px solid #dfe4ea',
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    background: visibility === 'PRIVATE' ? '#7f8c8d' : '#fff',
                                    color: visibility === 'PRIVATE' ? '#fff' : '#2c3e50',
                                }}
                            >
                                Private
                            </button>
                        </div>
                    ) : null}
                </div>
                <Button close onClick={on_close} />
            </div>

            {/* Mode advisory banner */}
            <div style={{ padding: '6px 14px', fontSize: 11, color: act ? '#c0392b' : '#7f8c8d', background: act ? '#fdf3e7' : '#f8f9fa', borderBottom: '1px solid #ecf0f1' }}>
                {act
                    ? '⚡ Act mode — the AI can propose state changes. You must confirm each one before it applies.'
                    : '🗣 Talk mode — read-only. The AI can\'t change anything; switch to Act if you want it to draft state changes.'}
            </div>

            {err ? <div style={{ padding: '8px 14px', color: '#c0392b', fontSize: 12 }}>{err}</div> : null}

            <div ref={scroll_ref} style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                {messages.length === 0 && !busy ? (
                    <div style={{ color: '#7f8c8d', fontSize: 13 }}>
                        {is_portfolio
                            ? (act
                                ? 'Across your portfolio. Try: "accept all DESIGN findings I\'ve discussed in calls this week" — each change goes through a confirmation card before applying.'
                                : 'Across your portfolio. Try: "what should I work on first today?" or "what\'s the common theme across this week\'s HIGH severity findings?"')
                            : (act
                                ? 'Ask the AI about this client. In Act mode you can also ask for status changes ("accept all DESIGN findings" etc) — every change goes through a confirmation card.'
                                : 'Ask anything about this client. A new fact ("they hired a new designer last week") gets saved to client memory for the next analysis.')}
                    </div>
                ) : null}
                {messages.map((m) => (
                    <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: m.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                        {/* Per-message author label on SHARED org threads only
                            (where multiple users may contribute). PRIVATE org
                            and portfolio chats are inherently single-author so
                            the label adds noise. */}
                        {visibility === 'SHARED' && m.role === 'USER' && m.userId ? (
                            <div style={{ fontSize: 10, color: '#7f8c8d', marginBottom: 2, paddingRight: 4 }}>
                                {format_author(m.userId, authors)}
                            </div>
                        ) : null}
                        {/* Scope chip on portfolio threads — shows which tab
                            (My/Team/All) was active when this USER message was
                            sent. Keeps the unified-thread reasoning honest:
                            past turns are interpretable in their original scope
                            even when the rep has since switched tabs. */}
                        {is_portfolio && m.role === 'USER' && m.scopeAtSend ? (
                            <Badge
                                color={m.scopeAtSend === 'my' ? 'primary' : (m.scopeAtSend === 'team' ? 'info' : 'warning')}
                                style={{ fontWeight: 400, fontSize: 10, marginBottom: 2 }}
                            >
                                {m.scopeAtSend === 'my' ? 'My' : (m.scopeAtSend === 'team' ? 'Team' : 'All')}
                            </Badge>
                        ) : null}
                        <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 10, background: m.role === 'USER' ? '#e8f0fe' : '#f5f6f7', color: '#2c3e50', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                            {m.content}
                            {m.role === 'USER' && m.becameRepInputId ? (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#1e8449' }}>
                                    ✓ saved to client memory — will influence next analysis
                                </div>
                            ) : null}
                        </div>
                        {m.role === 'ASSISTANT' ? (
                            <CorpusSources
                                citations={m.citations}
                                ctx={{ scope: is_portfolio ? portfolio_scope : 'client', clientId: store_id }}
                            />
                        ) : null}
                    </div>
                ))}

                {/* Proposal cards: surface the LLM's Act-mode proposals
                    inline. Apply commits via the same PATCH semantics as
                    manual UI. Dismiss removes without applying. */}
                {proposals.length > 0 ? (
                    <div style={{ border: '1px solid #f1c40f', background: '#fff8e1', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <strong style={{ fontSize: 12 }}>
                                ⚡ {proposals.length} proposed change(s) — review and apply
                            </strong>
                            <Button size='sm' color='warning' onClick={apply_all}>
                                Apply all
                            </Button>
                        </div>
                        {proposals.map((p) => (
                            <div key={p.id} style={{ borderTop: '1px solid #f1c40f44', padding: '6px 0', fontSize: 12 }}>
                                <div>
                                    <Badge color='info' style={{ marginRight: 4, fontSize: 10 }}>{p.kind}</Badge>
                                    <span style={{ color: '#7f8c8d' }}>{p.targetType}:{p.targetId.slice(0, 10)}…</span>
                                </div>
                                <div style={{ marginTop: 2 }}>
                                    <strong>{p.payload && Object.entries(p.payload).map(([k, v]) => `${k}=${typeof v === 'string' ? `"${v.slice(0, 60)}"` : v}`).join(', ')}</strong>
                                </div>
                                {p.rationale ? (
                                    <div style={{ color: '#7f8c8d', fontStyle: 'italic', marginTop: 2 }}>
                                        {p.rationale}
                                    </div>
                                ) : null}
                                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                    <Button size='sm' color='success' onClick={() => apply_proposal(p.id)}>Apply</Button>
                                    <Button size='sm' color='secondary' outline onClick={() => dismiss_proposal(p.id)}>Dismiss</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}

                {busy ? <div style={{ color: '#7f8c8d', fontSize: 12 }}>Thinking…</div> : null}
            </div>

            <div style={{ borderTop: '1px solid #ecf0f1', padding: 10, display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <Input
                    type='textarea'
                    rows={2}
                    placeholder={act ? 'In Act mode: tell the AI what to change…' : 'Ask the AI about this client… (Enter to send, Shift+Enter for newline)'}
                    value={draft}
                    onChange={(e) => set_draft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    disabled={busy}
                    style={{ resize: 'none', fontSize: 13, flex: 1, minWidth: 0 }}
                />
                <Button
                    color='primary'
                    onClick={send}
                    disabled={busy || !draft.trim()}
                    style={{ flexShrink: 0, alignSelf: 'flex-end' }}
                >
                    Send
                </Button>
            </div>
            <TaskCreateModal
                open={task_modal.open}
                store_id={task_modal.storeId}
                prefill={task_modal.prefill}
                on_close={() => set_task_modal({ open: false, prefill: null, storeId: null })}
                on_created={() => {
                    set_task_modal({ open: false, prefill: null, storeId: null });
                    set_messages((prev) => [...prev, {
                        id: 'sys-' + Date.now(),
                        role: 'ASSISTANT',
                        content: '✓ Task created.',
                        createdAt: new Date().toISOString(),
                    }]);
                }}
            />
        </div>
    );
};

export default ChatDrawer;
